// src/components/Analytics/PracticaResults.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';

const DOCTOR_NAMES = {
  sofia_campos: { name: 'Dra. Sofía Campos', specialty: 'Medicina General', emoji: '👩‍⚕️', color: '#10B981', difficulty: 'Fácil' },
  ricardo_navarro: { name: 'Dr. Ricardo Navarro', specialty: 'Cardiología', emoji: '👨‍⚕️', color: '#F59E0B', difficulty: 'Media' },
  elena_vega: { name: 'Dra. Elena Vega', specialty: 'Endocrinología', emoji: '👩‍⚕️', color: '#EF4444', difficulty: 'Media-Alta' },
  marcos_fuentes: { name: 'Dr. Marcos Fuentes', specialty: 'Medicina Interna', emoji: '🧑‍⚕️', color: '#7C3AED', difficulty: 'Difícil' }
};

const CATEGORY_LABELS = {
  saludo_presentacion: 'Saludo y Presentación',
  manejo_tiempo: 'Manejo del Tiempo',
  apertura_rapport: 'Apertura y Rapport',
  mensaje_clave: 'Mensaje Clave',
  datos_clinicos: 'Datos Clínicos',
  manejo_objeciones: 'Manejo de Objeciones',
  cierre_compromiso: 'Cierre y Compromiso',
  comunicacion_general: 'Comunicación General'
};

// Styled Components
const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 24px;
`;

const CardTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 12px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: ${props => props.active ? '#14B8A6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  &:hover { background: ${props => props.active ? '#0D9488' : '#e5e7eb'}; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 16px;
  background: ${props => props.background || '#f8fafc'};
  border-radius: 8px;
  border-left: 4px solid ${props => props.color || '#14B8A6'};
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DoctorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid ${props => props.color || '#14B8A6'};
  margin-bottom: 12px;
`;

const BarContainer = styled.div`
  margin-bottom: 12px;
`;

const BarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 13px;
`;

const BarTrack = styled.div`
  height: 10px;
  background: #e5e7eb;
  border-radius: 5px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => props.color};
  border-radius: 5px;
  transition: width 0.8s ease;
`;

const SesionItem = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => {
    const s = props.score;
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    return '#ef4444';
  }};
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #f0fdfa; }
`;

const ScoreBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    if (props.score >= 80) return '#dcfce7';
    if (props.score >= 60) return '#fef3c7';
    return '#fee2e2';
  }};
  color: ${props => {
    if (props.score >= 80) return '#166534';
    if (props.score >= 60) return '#92400e';
    return '#991b1b';
  }};
`;

const RankingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background: #f8fafc;
  color: #374151;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 2px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #4b5563;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-size: 15px;
`;

const DetailPanel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-top: 12px;
`;

const PracticaResults = ({ documentId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSesion, setSelectedSesion] = useState(null);
  const [doctorFilter, setDoctorFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, sesRes, rankRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/analytics/practica-analytics.php?document_id=${documentId}&type=overview`),
        axios.get(`${API_BASE_URL}/analytics/practica-analytics.php?document_id=${documentId}&type=sesiones`),
        axios.get(`${API_BASE_URL}/analytics/practica-analytics.php?document_id=${documentId}&type=ranking`)
      ]);
      setOverview(ovRes.data);
      setSesiones(sesRes.data?.sesiones || []);
      setRanking(rankRes.data?.ranking || []);
    } catch (err) {
      console.error('Error cargando analytics de práctica:', err);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtDuration = (s) => {
    if (!s) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const barColor = (score) => score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';

  if (loading) return <Card><EmptyState>Cargando datos de práctica...</EmptyState></Card>;

  const stats = overview?.stats;
  const noData = !stats || parseInt(stats.total_sesiones) === 0;

  if (noData) return <Card><EmptyState>No hay sesiones de práctica registradas para este documento.</EmptyState></Card>;

  return (
    <div>
      <TabContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Resumen</Tab>
        <Tab active={activeTab === 'sesiones'} onClick={() => setActiveTab('sesiones')}>Sesiones</Tab>
        <Tab active={activeTab === 'ranking'} onClick={() => setActiveTab('ranking')}>Ranking</Tab>
      </TabContainer>

      {activeTab === 'overview' && (
        <>
          {/* Stats generales */}
          <StatsGrid>
            <StatItem color="#14B8A6">
              <StatValue>{stats.total_sesiones}</StatValue>
              <StatLabel>Sesiones</StatLabel>
            </StatItem>
            <StatItem color="#6366F1">
              <StatValue>{stats.usuarios_unicos}</StatValue>
              <StatLabel>Usuarios</StatLabel>
            </StatItem>
            <StatItem color={barColor(parseFloat(stats.promedio_general))}>
              <StatValue>{stats.promedio_general || 0}</StatValue>
              <StatLabel>Promedio</StatLabel>
            </StatItem>
            <StatItem color="#10B981">
              <StatValue>{stats.mejor_puntaje || 0}</StatValue>
              <StatLabel>Mejor</StatLabel>
            </StatItem>
            <StatItem color="#F59E0B">
              <StatValue>{fmtDuration(parseInt(stats.duracion_promedio))}</StatValue>
              <StatLabel>Duración Prom.</StatLabel>
            </StatItem>
          </StatsGrid>

          {/* Distribución */}
          <Card>
            <CardTitle>Distribución de Resultados</CardTitle>
            <StatsGrid>
              <StatItem color="#10B981" background="#f0fdf4">
                <StatValue>{stats.excelentes || 0}</StatValue>
                <StatLabel>Excelentes (80+)</StatLabel>
              </StatItem>
              <StatItem color="#F59E0B" background="#fffbeb">
                <StatValue>{stats.buenos || 0}</StatValue>
                <StatLabel>Buenos (60-79)</StatLabel>
              </StatItem>
              <StatItem color="#EF4444" background="#fef2f2">
                <StatValue>{stats.necesitan_mejora || 0}</StatValue>
                <StatLabel>A mejorar (&lt;60)</StatLabel>
              </StatItem>
            </StatsGrid>
          </Card>

          {/* Promedios por categoría */}
          {overview?.category_averages && Object.keys(overview.category_averages).length > 0 && (
            <Card>
              <CardTitle>Promedio por Categoría</CardTitle>
              {Object.entries(overview.category_averages).map(([key, score]) => (
                <BarContainer key={key}>
                  <BarLabel>
                    <span style={{ color: '#374151' }}>{CATEGORY_LABELS[key] || key}</span>
                    <span style={{ color: barColor(score), fontWeight: 600 }}>{score}</span>
                  </BarLabel>
                  <BarTrack>
                    <BarFill width={score} color={barColor(score)} />
                  </BarTrack>
                </BarContainer>
              ))}
            </Card>
          )}

          {/* Por doctor */}
          {overview?.by_doctor?.length > 0 && (
            <Card>
              <CardTitle>Por Doctor</CardTitle>
              {overview.by_doctor.map(d => {
                const doc = DOCTOR_NAMES[d.doctor_id] || {};
                return (
                  <DoctorCard key={d.doctor_id} color={doc.color}>
                    <span style={{ fontSize: '1.5rem' }}>{doc.emoji || '👤'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>{doc.name || d.doctor_id}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{doc.specialty} — {doc.difficulty}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: '1.2rem', color: barColor(parseFloat(d.promedio)) }}>{d.promedio}</div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{d.sesiones} sesiones / {d.usuarios} usuarios</div>
                    </div>
                  </DoctorCard>
                );
              })}
            </Card>
          )}
        </>
      )}

      {activeTab === 'sesiones' && (
        <Card>
          <CardTitle>Últimas Sesiones de Práctica</CardTitle>
          {/* Filtro por doctor */}
          <div style={{ marginBottom: '16px' }}>
            <select value={doctorFilter} onChange={e => setDoctorFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
              <option value="">Todos los doctores</option>
              {Object.entries(DOCTOR_NAMES).map(([id, doc]) => (
                <option key={id} value={id}>{doc.emoji} {doc.name}</option>
              ))}
            </select>
          </div>

          {sesiones
            .filter(s => !doctorFilter || s.doctor_id === doctorFilter)
            .map(s => {
              const doc = DOCTOR_NAMES[s.doctor_id] || {};
              const isSelected = selectedSesion?.id === s.id;
              return (
                <div key={s.id}>
                  <SesionItem score={parseFloat(s.overall_score)} onClick={() => setSelectedSesion(isSelected ? null : s)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{s.user_nombre || s.user_email}</span>
                        <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{doc.emoji} {doc.name}</span>
                      </div>
                      <ScoreBadge score={parseFloat(s.overall_score)}>{Math.round(s.overall_score)} pts</ScoreBadge>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.8rem', color: '#9ca3af' }}>
                      <span>Duración: {fmtDuration(parseInt(s.duration_seconds))}</span>
                      <span>{new Date(s.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </SesionItem>

                  {isSelected && (
                    <DetailPanel>
                      {/* Barras de categorías */}
                      {s.scores && Object.entries(s.scores).map(([key, score]) => (
                        <BarContainer key={key}>
                          <BarLabel>
                            <span style={{ color: '#374151', fontSize: '12px' }}>{CATEGORY_LABELS[key] || key}</span>
                            <span style={{ color: barColor(score), fontWeight: 600, fontSize: '12px' }}>{score}</span>
                          </BarLabel>
                          <BarTrack><BarFill width={score} color={barColor(score)} /></BarTrack>
                        </BarContainer>
                      ))}
                      {/* Feedback */}
                      {s.feedback && (
                        <div style={{ background: '#f0fdfa', borderRadius: '8px', padding: '12px', marginTop: '12px', fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>
                          {s.feedback}
                        </div>
                      )}
                      {/* Fortalezas y mejoras */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                        {s.fortalezas?.length > 0 && (
                          <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10B981', marginBottom: '4px' }}>Fortalezas</div>
                            {s.fortalezas.map((f, i) => <div key={i} style={{ fontSize: '0.8rem', color: '#374151' }}>+ {f}</div>)}
                          </div>
                        )}
                        {s.areas_mejora?.length > 0 && (
                          <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#F59E0B', marginBottom: '4px' }}>Áreas de Mejora</div>
                            {s.areas_mejora.map((a, i) => <div key={i} style={{ fontSize: '0.8rem', color: '#374151' }}>- {a}</div>)}
                          </div>
                        )}
                      </div>
                    </DetailPanel>
                  )}
                </div>
              );
            })}
          {sesiones.filter(s => !doctorFilter || s.doctor_id === doctorFilter).length === 0 && (
            <EmptyState>No hay sesiones con este filtro.</EmptyState>
          )}
        </Card>
      )}

      {activeTab === 'ranking' && (
        <Card>
          <CardTitle>Ranking de Usuarios</CardTitle>
          {ranking.length === 0 ? (
            <EmptyState>No hay datos de ranking.</EmptyState>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <RankingTable>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Usuario</Th>
                    <Th>Prácticas</Th>
                    <Th>Promedio</Th>
                    <Th>Mejor</Th>
                    <Th>Duración Prom.</Th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map((r, i) => (
                    <tr key={r.user_id}>
                      <Td style={{ fontWeight: 600 }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </Td>
                      <Td>
                        <div style={{ fontWeight: 500 }}>{r.user_nombre || 'Sin nombre'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{r.user_email}</div>
                      </Td>
                      <Td>{r.total_practicas}</Td>
                      <Td><ScoreBadge score={parseFloat(r.promedio)}>{r.promedio}</ScoreBadge></Td>
                      <Td>{r.mejor_puntaje}</Td>
                      <Td>{fmtDuration(parseInt(r.duracion_promedio))}</Td>
                    </tr>
                  ))}
                </tbody>
              </RankingTable>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default PracticaResults;
